import type { ComponentChildren } from "preact";
import type { Field } from "@fobu/schema";
import type { AnswerIssue } from "@fobu/engine";
import { useFormEnv } from "../lib/context";
import { Markdown } from "../lib/markdown";
import { t } from "../lib/i18n";

export const fieldDomId = (fieldId: string) => `fb-${fieldId}`;

/** Feldtypen, die als Gruppe (fieldset/legend) ausgezeichnet werden. */
const GROUP_TYPES = new Set(["single_choice", "multiple_choice", "yes_no", "rating", "nps", "scale", "matrix", "poll", "address", "date_range", "amount", "order", "sepa", "ranking", "consent", "signature", "group"]);

export interface ShellProps {
  field: Field;
  issues: AnswerIssue[] | undefined;
  /** Conversational: große Frage, Nummer, Hinweis. */
  large?: boolean;
  number?: number;
  hint?: string;
  children: (a11y: { id: string; describedBy: string | undefined; invalid: boolean; labelId: string }) => ComponentChildren;
}

export function FieldShell({ field, issues, large, number, hint, children }: ShellProps) {
  const { tpl } = useFormEnv();
  const id = fieldDomId(field.id);
  const labelId = `${id}-label`;
  const descId = field.description ? `${id}-desc` : undefined;
  const errId = issues?.length ? `${id}-err` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [descId, hintId, errId].filter(Boolean).join(" ") || undefined;
  const invalid = !!issues?.length;
  const isGroup = GROUP_TYPES.has(field.type);

  const labelContent = (
    <>
      {number !== undefined && <span class="fb-qnum" aria-hidden="true">{number}</span>}
      <Markdown text={tpl(field.label)} />
      {field.required ? (
        <span class="fb-req" aria-hidden="true"> *</span>
      ) : null}
      {field.required && <span class="fb-sr"> ({t.required})</span>}
    </>
  );

  const body = (
    <>
      {field.description && (
        <div id={descId} class="fb-desc">
          <Markdown text={tpl(field.description)} />
        </div>
      )}
      {hint && (
        <p id={hintId} class="fb-hint">
          {hint}
        </p>
      )}
      <div class="fb-control">{children({ id, describedBy, invalid, labelId })}</div>
      {invalid && (
        <p id={errId} class="fb-error">
          <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16">
            <circle cx="8" cy="8" r="7" fill="currentColor" />
            <path d="M8 4.5v4.2M8 10.8v.7" stroke="var(--fb-surface)" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          {issues![0]!.message}
        </p>
      )}
    </>
  );

  const cls = `fb-field fb-field--${field.type}${large ? " fb-field--large" : ""}${invalid ? " is-invalid" : ""}`;
  if (isGroup) {
    return (
      <fieldset class={cls} id={`${id}-wrap`} aria-describedby={describedBy} aria-invalid={invalid || undefined}>
        <legend class="fb-label" id={labelId}>
          {labelContent}
        </legend>
        {body}
      </fieldset>
    );
  }
  return (
    <div class={cls} id={`${id}-wrap`}>
      <label class="fb-label" id={labelId} for={id}>
        {labelContent}
      </label>
      {body}
    </div>
  );
}
